<template>
  <div class="container mt-4">
    <div class="page-header">
      <div class="page-header__titles">
        <span class="page-header__accent"></span>
        <div>
          <h2 class="page-header__title">Reported Review</h2>
          <p class="page-header__subtitle">Full details of the flagged review and the conversation between both parties.</p>
        </div>
      </div>
      <router-link :to="{ name: 'AllReviews' }" class="rform__btn rform__btn--ghost">
        <i class="fa fa-arrow-left"></i> Back to Reviews
      </router-link>
    </div>

    <LoadingBar v-if="loading" message="Loading review..." />
    <div v-else-if="loadError" class="review-detail-error">{{ loadError }}</div>

    <template v-else-if="review">
      <div class="review-detail-grid">
        <!-- ============ Review card ============ -->
        <div class="review-detail-card">
          <div class="review-detail-card__header">
            <h4>Review Details</h4>
            <span class="review-status-badge" v-if="review.status == 'reported'" :class="`review-status-badge--${review.status}`">
              {{ formatStatus(review.status) }}
            </span>
          </div>

          <div class="review-detail__row">
            <span class="review-detail__label">Supplier Reviewed</span>
            <strong>{{ personName(review.supplier) }}</strong>
          </div>
          <div class="review-detail__row">
            <span class="review-detail__label">Reviewer</span>
            <strong>{{ personName(review.reviewer) }}</strong>
          </div>
          <div class="review-detail__row">
            <span class="review-detail__label">Rating</span>
            <span class="review-stars">
              <span v-for="n in 5" :key="n" class="review-star" :class="{ 'review-star--filled': n <= review.stars }">★</span>
            </span>
          </div>
          <div class="review-detail__row">
            <span class="review-detail__label">Review Date</span>
            <span>{{ formatDate(review.created_at) }}</span>
          </div>

          <div v-if="review.review" class="review-detail__block">
            <span class="review-detail__label">Review</span>
            <p>{{ review.review }}</p>
          </div>
          <div v-if="review.comments" class="review-detail__block">
            <span class="review-detail__label">Additional Comments</span>
            <p>{{ review.comments }}</p>
          </div>

          <!-- ============ Report Attachments ============ -->
          <div v-if="attachments && attachments.length" class="review-detail__block">
            <span class="review-detail__label">Report Attachments</span>
            <div class="review-detail__attachments">
              <div v-for="file in attachments" :key="file.id" class="review-detail__attachment-item">
                <a :href="getFileUrl(file.name)" target="_blank" class="review-detail__attachment-link">
                  <i class="fa fa-paperclip"></i> 
                  {{ file.org_name || file.name.split('/').pop() }}
                </a>
                <span class="review-detail__attachment-size" v-if="file.size">
                  ({{ formatFileSize(file.size) }})
                </span>
              </div>
            </div>
          </div>

          <!-- ============ Report Info ============ -->
          <div class="review-detail__block review-detail__block--flagged">
            <span class="review-detail__label">
              <i class="fa fa-flag text-danger"></i> Reported By
            </span>
            <strong>{{ personName(review.reporter) }}</strong>
            <span v-if="review.reported_at" class="text-muted fs-12 d-block mt-1">
              {{ formatDate(review.reported_at) }}
            </span>
          </div>
          <div v-if="review.report_reason" class="review-detail__block review-detail__block--flagged">
            <span class="review-detail__label">Report Reason</span>
            <p>{{ review.report_reason }}</p>
          </div>

          <div class="review-actions">
            <button
              type="button"
              class="rform__btn--danger"
              @click="showDeleteModal = true"
              :disabled="deleting"
            >
              <i class="fa fa-trash"></i> Delete Review
            </button>
          </div>
        </div>
<ConfirmDeleteModal
  v-model="showDeleteModal"
  title="Delete Review"
  :item-name="review ? `review by ${personName(review.reviewer)}` : ''"
  :loading="deleting"
  @confirm="deleteReview"
/>
        <!-- ============ Conversation card ============ -->
        <div class="review-detail-card">
          <div class="review-detail-card__header">
            <h4>Conversation</h4>
            <span v-if="conversationId" class="text-muted fs-12">Between reviewer &amp; supplier</span>
          </div>

          <div ref="messageList" class="conversation-messages">
            <p v-if="loadingConversation" class="conversation-empty">Loading conversation…</p>
            <p v-else-if="conversationError" class="conversation-empty conversation-empty--error">
              {{ conversationError }}
            </p>
            <p v-else-if="!messages.length" class="conversation-empty">
              No conversation found between these two parties.
            </p>

            <div v-for="(msg, i) in messages" :key="i" class="chat-msg" :class="{ 'chat-msg--mine': msg.mine }">
              <span class="chat-msg__sender">{{ msg.mine ? personName(review.reviewer) : personName(review.supplier) }}</span>
              <p v-if="msg.text" class="chat-msg__text">{{ msg.text }}</p>
              <div v-if="msg.attachment" class="chat-msg__attachment">
                <img v-if="msg.attachment.type?.startsWith('image/')" :src="msg.attachment.url" class="chat-msg__image" />
                <a v-else :href="msg.attachment.url" target="_blank" class="chat-msg__file">
                  📎 {{ msg.attachment.name }}
                </a>
              </div>
              <span class="chat-msg__time">{{ msg.time }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import LoadingBar from '@/components/utils/LoadingBar.vue'
import { form_api_submit } from '@/utils/FormSubmit'
import { resolveFileUrl as resolveAssetFileUrl } from '@/utils/fileUrl'
import ConfirmDeleteModal from '@/components/utils/ConfirmDeleteModal.vue'
export default {
  name: 'ReviewDetail',
  components: { LoadingBar ,
    ConfirmDeleteModal,
  },

  data() {
    return {
      loading: true,
      loadError: '',
      review: null,
      attachments: [],
      submitting: false,

     showDeleteModal: false,
      deleting: false,

      conversationId: null,
      loadingConversation: false,
      conversationError: '',
      messages: [],
    }
  },

  computed: {
    canModerate() {
      return this.review && (this.review.status === 'pending' || this.review.status === 'reported')
    }
  },

  async mounted() {
    const id = this.$route.params.id
    if (!id) {
      this.loadError = 'No review specified.'
      this.loading = false
      return
    }
    await this.loadReview(id)
    if (this.review) {
      await this.loadConversation()
    }
  },

  methods: {
    personName(person) {
      if (!person) return '—'
      return person.company_name || [person.first_name, person.last_name].filter(Boolean).join(' ') || '—'
    },
    
    formatStatus(status) {
      const map = { pending: 'Pending', approved: 'Approved', rejected: 'Rejected', reported: 'Reported' }
      return map[status] || status || 'Pending'
    },
    
    formatDate(value) {
      if (!value) return '—'
      const d = new Date(value)
      if (Number.isNaN(d.getTime())) return value
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    
    formatFileSize(bytes) {
      if (!bytes) return ''
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return (bytes / Math.pow(1024, i)).toFixed(1) + ' ' + sizes[i]
    },
    
    getFileUrl(path) {
      return resolveAssetFileUrl(path)
    },

    async loadReview(id) {
      this.loading = true
      this.loadError = ''

      const result = await form_api_submit({
        url: `reviews/${id}`,
        method: 'GET',
        show_success_toast: false,
        show_error_toast: false,
      })

      if (result.success) {
        const data = result.data
        this.review = data.review
        this.attachments = data.attachments || []
      } else {
        this.loadError = 'Could not load this review. Please try again.'
      }

      this.loading = false
    },

    async loadConversation() {
      const reviewerId = this.review?.reviewed_by ?? this.review?.reviewer?.id
      const supplierId = this.review?.supplier_id ?? this.review?.supplier?.id
      if (!reviewerId || !supplierId) return

      this.loadingConversation = true
      this.conversationError = ''

      const startResult = await form_api_submit({
        url: 'chat/start',
        method: 'POST',
        data: { sender_id: reviewerId, target_supplier_id: supplierId },
        show_success_toast: false,
        show_error_toast: false,
      })

      if (!startResult.success) {
        this.conversationError = 'Could not load the conversation.'
        this.loadingConversation = false
        return
      }

      this.conversationId = startResult.data?.id ?? null
      if (!this.conversationId) {
        this.loadingConversation = false
        return
      }

      const result = await form_api_submit({
        url: `chat/${this.conversationId}/messages`,
        method: 'GET',
        data: { sender_id: reviewerId },
        show_success_toast: false,
        show_error_toast: false,
      })

      this.loadingConversation = false
      if (!result.success || !Array.isArray(result.data)) return

      this.messages = this.mapAllConversationMessages(result.data, reviewerId)
      this.$nextTick(this.scrollMessagesToEnd)
    },

    mapAllConversationMessages(messages, reviewerId) {
      const sorted = [...messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      return sorted.map((msg) => ({
        mine: Number(msg.sender_supplier_id) === Number(reviewerId),
        text: msg.body || '',
        attachment: msg.attachment_url
          ? { url: this.getFileUrl(msg.attachment_url), name: msg.attachment_name, type: msg.type }
          : null,
        time: msg.created_at
          ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '',
      }))
    },

    scrollMessagesToEnd() {
      const list = this.$refs.messageList
      if (list) list.scrollTop = list.scrollHeight
    },

    // async approveReview() {
    //   if (!confirm('Are you sure you want to approve this review?')) return

    //   this.submitting = true
    //   const result = await form_api_submit({
    //     url: `reviews/${this.review.id}/approve`,
    //     method: 'POST',
    //     show_success_toast: true,
    //     show_error_toast: true,
    //   })

    //   this.submitting = false
    //   if (result.success) {
    //     this.review = result.data.review
    //     await this.loadReview(this.$route.params.id)
    //   }
    // },

    // async rejectReview() {
    //   const reason = prompt('Please provide a reason for rejecting this review:')
    //   if (reason === null) return

    //   this.submitting = true
    //   const result = await form_api_submit({
    //     url: `reviews/${this.review.id}/reject`,
    //     method: 'POST',
    //     data: { rejection_reason: reason },
    //     show_success_toast: true,
    //     show_error_toast: true,
    //   })

    //   this.submitting = false
    //   if (result.success) {
    //     this.review = result.data.review
    //     await this.loadReview(this.$route.params.id)
    //   }
    // },

    async deleteReview() {
      this.deleting = true
      const result = await form_api_submit({
        url: `reviews/delete/${this.review.id}`,
        method: 'DELETE',
        show_success_toast: true,
        show_error_toast: true,
      })
      this.deleting = false
      this.showDeleteModal = false

      if (result.success) {
        this.$router.push({ name: 'AllReviews' })
      }
    },
  
  },
}
</script>

<style scoped>
.review-detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  align-items: start;
}

@media (max-width: 900px) {
  .review-detail-grid {
    grid-template-columns: 1fr;
  }
}

.review-detail-card {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 20px;
}

.review-detail-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.review-detail-card__header h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.review-detail__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-border);
  font-size: 13.5px;
}

.review-detail__label {
  color: var(--color-text-muted);
  font-weight: 600;
  font-size: 12px;
}

.review-detail__block {
  margin-top: 14px;
}

.review-detail__block p {
  margin: 6px 0 0;
  font-size: 13.5px;
  color: var(--color-text-dark);
  line-height: 1.5;
}

.review-detail__block--flagged {
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(179, 38, 30, 0.05);
  border: 1px solid rgba(179, 38, 30, 0.18);
}

.review-detail__attachments {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 6px;
}

.review-detail__attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
}

.review-detail__attachment-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-secondary);
  text-decoration: none;
}

.review-detail__attachment-link:hover {
  text-decoration: underline;
}

.review-detail__attachment-size {
  font-size: 11px;
  color: var(--color-text-muted);
}

.review-stars {
  display: inline-flex;
  gap: 1px;
}

.review-star {
  color: var(--color-border, #e2e2e2);
  font-size: 14px;
}

.review-star--filled {
  color: #ec993a;
}

.review-status-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: capitalize;
}

.review-status-badge--pending { background: rgba(236, 153, 58, 0.14); color: #a15b0a; }
.review-status-badge--approved { background: rgba(26, 127, 75, 0.1); color: #1a7f4b; }
.review-status-badge--rejected { background: rgba(108, 117, 125, 0.12); color: #6c757d; }
.review-status-badge--reported { background: rgba(179, 38, 30, 0.1); color: #b3261e; }
.review-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
}

.rform__btn--danger {
  background: #b3261e;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rform__btn--danger:hover:not(:disabled) {
  background: #8a1d16;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(179, 38, 30, 0.3);
}

.rform__btn--danger:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.rform__btn--success {
  background: #1a7f4b;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rform__btn--success:hover:not(:disabled) {
  background: #0f5c36;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(26, 127, 75, 0.3);
}

.rform__btn--danger {
  background: #b3261e;
  color: #fff;
  border: none;
  padding: 8px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rform__btn--danger:hover:not(:disabled) {
  background: #8a1d16;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(179, 38, 30, 0.3);
}

.rform__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.review-detail-error {
  padding: 40px;
  text-align: center;
  color: #b3261e;
  font-size: 13.5px;
}

.conversation-messages {
  max-height: 480px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 4px;
}

.conversation-empty {
  text-align: center;
  color: var(--color-text-muted);
  font-size: 13px;
  margin-top: 20px;
}

.conversation-empty--error {
  color: #b3261e;
}

.chat-msg {
  align-self: flex-start;
  max-width: 85%;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  border-radius: 12px 12px 12px 4px;
  padding: 8px 10px;
  font-size: 12.5px;
  color: var(--color-text-dark);
}

.chat-msg--mine {
  align-self: flex-end;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  border-color: transparent;
  border-radius: 12px 12px 4px 12px;
}

.chat-msg__sender {
  display: block;
  font-size: 10.5px;
  font-weight: 700;
  opacity: 0.75;
  margin-bottom: 2px;
}

.chat-msg__text {
  margin: 0 0 4px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.chat-msg__image {
  max-width: 180px;
  border-radius: 8px;
  margin-top: 4px;
}

.chat-msg__file {
  font-size: 12px;
  color: inherit;
}

.chat-msg__time {
  display: block;
  font-size: 10px;
  opacity: 0.6;
  text-align: right;
  margin-top: 2px;
}
</style>